import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Chip,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  DragIndicator as DragIndicatorIcon,
  Event as EventIcon,
  ExitToApp as ExitToAppIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  FitnessCenter as FitnessCenterIcon,
  People as PeopleIcon,
  ShoppingBasket as ShoppingBasketIcon,
} from '@material-ui/icons';

import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { formatPrice } from '../../../shared/utils/format-price.util';
import { formatShortDate } from '../../../shared/utils/format-date.util';
import { TableBodySkeleton } from '../../../shared/components/Tables/TableBodySkeleton.component';
import { ROWS_PER_PAGE_OPTIONS } from '../../../shared/constants/table.constants';
import { Basket } from '../../types/basket.type';
import { BasketProduct } from '../../types/basket-product.type';
import { ADMIN_BASKETS_APP_PATHS, BASKET_IMAGE_PATH } from '../../constants/baskets-app-paths.constants';
import { ADMIN_PRODUCTS_APP_PATHS } from '../../../products/constants/products-app-paths.constants';
import StatusChip from '../../../shared/components/Chips/StatusChip.component';
import { StockChip } from '../../../shared/components/Chips/StockChip.component';

interface TableHeader {
  key: string;
  label: string;
  ariaLabel?: string;
  minWidth: number;
  align: 'left' | 'right' | 'center';
}

interface BasketsTableProps {
  baskets: Basket[];
  totalCount: number;
  page: number;
  rowsPerPage: number;
  isLoading: boolean;
  isDragEnabled: boolean;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onPositionsChange?: (baskets: { id: string; position: number }[]) => void;
  onToggleActive: (basketId: string, isActive: boolean) => Promise<boolean>;
  onProductsReorder: (basketId: string, productIds: string[]) => Promise<void>;
}

const BASE_HEADERS: TableHeader[] = [
  { key: 'actions', label: '', ariaLabel: 'Actions', minWidth: 80, align: 'center' },
  { key: 'image', label: '', minWidth: 70, align: 'center' },
  { key: 'name', label: 'Panier', minWidth: 300, align: 'left' },
  { key: 'subCategory', label: 'Sous-rubrique', minWidth: 200, align: 'left' },
  { key: 'price', label: 'Prix', minWidth: 100, align: 'right' },
  { key: 'stock', label: 'Stock', minWidth: 80, align: 'center' },
  { key: 'active', label: 'Statut', minWidth: 80, align: 'center' },
];

function getHeaders(isDragEnabled: boolean): TableHeader[] {
  if (!isDragEnabled) return BASE_HEADERS;

  const actionsHeader: TableHeader = {
    key: 'actions',
    label: '',
    ariaLabel: 'Actions',
    minWidth: 120,
    align: 'center',
  };
  return [actionsHeader, ...BASE_HEADERS.filter((header) => header.key !== 'actions')];
}

const PRODUCT_HEADERS: TableHeader[] = [
  { key: 'actions', label: '', ariaLabel: 'Actions', minWidth: 80, align: 'left' },
  { key: 'photo', label: 'Photo', minWidth: 60, align: 'center' },
  { key: 'name', label: 'Produit', minWidth: 300, align: 'left' },
  { key: 'weight', label: 'Poids', minWidth: 100, align: 'left' },
  { key: 'labels', label: 'Labels', minWidth: 100, align: 'left' },
];

const LABEL_NAMES: Record<string, string> = {
  organic: 'Bio',
  wild: 'Sauvage',
  french: 'Français',
  native: 'Local',
};

const useStyles = makeStyles((theme) => ({
  secondaryLine: {
    display: 'block',
    fontSize: 12,
    color: '#757575',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'left',
    gap: '1rem',
  },
  drag: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.4rem',
  },
  positionChip: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    fontWeight: 600,
    fontSize: 12,
    borderRadius: 4,
  },
  statusChip: {
    cursor: 'pointer',
    '& > .MuiChip-root': {
      cursor: 'pointer',
    },
  },
}));

const useRowStyles = makeStyles({
  basketRow: (props: { isOpen: boolean }) => ({
    '& > .MuiTableCell-root': {
      borderBottom: props.isOpen ? 0 : '1px solid rgba(224, 224, 224, 1)',
    },
  }),
  collapseRow: (props: { isOpen: boolean }) => ({
    '& > .MuiTableCell-root': props.isOpen
      ? { padding: '0.5rem', borderBottom: '1px solid rgba(224, 224, 224, 1)' }
      : { padding: '0 0.5rem', borderBottom: 0 },
  }),
  collapse: {
    '& .MuiPaper-root': {
      padding: '1rem',
    },
  },
});

const useSummaryStyles = makeStyles((theme) => ({
  header: {
    padding: theme.spacing(2),
    overflow: 'auto',
    display: 'flex',
    justifyContent: 'space-evenly',
    gap: '3rem',
    marginBottom: '0.5rem',
    '& > div': {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      '& > p': {
        fontWeight: 'bold',
        margin: '0',
        '& > span': {
          display: 'block',
          fontWeight: 'normal',
        },
      },
    },
  },
}));

function BasketStatusCell({
  basket,
  onToggleActive,
}: {
  basket: Basket;
  onToggleActive: (basketId: string, isActive: boolean) => void;
}) {
  const classes = useStyles();

  if (basket.archivedAt) {
    return (
      <TableCell align="center">
        <StatusChip isActive={basket.isActive} archived />
      </TableCell>
    );
  }

  return (
    <TableCell align="center">
      <span className={classes.statusChip} onClick={() => onToggleActive(basket.id, !basket.isActive)}>
        <StatusChip isActive={basket.isActive} />
      </span>
    </TableCell>
  );
}

function BasketHeaderSummary({ basket }: { basket: Basket }) {
  const classes = useSummaryStyles();

  const items = [
    {
      key: 'products',
      Icon: <ShoppingBasketIcon />,
      label: 'Produits',
      value: `${basket.products.length}`,
    },
    {
      key: 'persons',
      Icon: <PeopleIcon />,
      label: 'Personnes',
      value: `${basket.persons.from} - ${basket.persons.to}`,
    },
    {
      key: 'weight',
      Icon: <FitnessCenterIcon />,
      label: 'Poids',
      value: `${basket.weight.from} - ${basket.weight.to} ${basket.weight.unity.abbreviation}`,
    },
    {
      key: 'availability',
      Icon: <EventIcon />,
      label: 'Disponibilité',
      value: `${formatShortDate(basket.availability.from)} - ${formatShortDate(basket.availability.to)}`,
    },
  ];

  return (
    <Paper className={classes.header}>
      {items.map((item, index) => (
        <React.Fragment key={item.key}>
          {index > 0 && <Divider style={{ height: '50px' }} orientation="vertical" light />}
          <div>
            {item.Icon}
            <p>
              {item.label} : <span>{item.value}</span>
            </p>
          </div>
        </React.Fragment>
      ))}
    </Paper>
  );
}

function SortableBasketProductRow({ product, index }: { product: BasketProduct; index: number }) {
  const classes = useStyles();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: product.productId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <TableRow ref={setNodeRef} style={style} key={product.productId}>
      <TableCell align="left">
        <div className={classes.actions}>
          <div className={classes.drag}>
            <DragIndicatorIcon {...attributes} {...listeners} style={{ cursor: 'grab', opacity: 0.6 }} />
            <Chip className={classes.positionChip} size="small" label={index + 1} />
          </div>
          <IconButton
            size="small"
            aria-label="voir produit"
            component={Link}
            to={ADMIN_PRODUCTS_APP_PATHS.PRODUCT_DETAILS(product.productId)}
            target="_blank"
          >
            <ExitToAppIcon />
          </IconButton>
        </div>
      </TableCell>
      <TableCell align="center">
        <img src={BASKET_IMAGE_PATH(product.photo)} alt={product.name} width={40} height={40} />
      </TableCell>
      <TableCell align="left">
        {product.name}
        <span className={classes.secondaryLine}>Réf. {product.reference}</span>
      </TableCell>
      <TableCell align="left">
        <Chip
          size="small"
          label={`${product.weight.quantity} ${product.weight.abbreviation}`}
          style={{ backgroundColor: '#009688', color: '#ffffff', fontWeight: 600, fontSize: 12, borderRadius: 4 }}
        />
      </TableCell>
      <TableCell align="left">
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {Object.entries(product.labels)
            .filter(([, value]) => value)
            .map(([key]) => (
              <Chip
                key={key}
                size="small"
                label={LABEL_NAMES[key] ?? key}
                style={{
                  backgroundColor: '#4caf50',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: 12,
                  borderRadius: 4,
                }}
              />
            ))}
        </div>
      </TableCell>
    </TableRow>
  );
}

function BasketProductsTable({
  basketId,
  products,
  onProductsReorder,
}: {
  basketId: string;
  products: BasketProduct[];
  onProductsReorder: (basketId: string, productIds: string[]) => Promise<void>;
}) {
  const [localProducts, setLocalProducts] = useState(products);

  useEffect(() => {
    setLocalProducts(products);
  }, [products]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = localProducts.findIndex((basketProduct) => basketProduct.productId === active.id);
    const newIndex = localProducts.findIndex((basketProduct) => basketProduct.productId === over.id);

    const reordered = arrayMove(localProducts, oldIndex, newIndex);
    setLocalProducts(reordered);
    onProductsReorder(
      basketId,
      reordered.map((basketProduct) => basketProduct.productId)
    );
  };

  return (
    <Table size="small" stickyHeader style={{ border: 'solid 2px #ececec', borderRadius: '4px 4px 0 0' }}>
      <TableHead>
        <TableRow>
          {PRODUCT_HEADERS.map((header) => (
            <TableCell
              align={header.align}
              style={{ fontWeight: 'bold', minWidth: header.minWidth }}
              key={header.key}
              aria-label={header.ariaLabel}
            >
              {header.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={localProducts.map((basketProduct) => basketProduct.productId)}
          strategy={verticalListSortingStrategy}
        >
          <TableBody>
            {localProducts.map((product, index) => (
              <SortableBasketProductRow key={product.productId} product={product} index={index} />
            ))}
          </TableBody>
        </SortableContext>
      </DndContext>
    </Table>
  );
}

function SortableBasketRow({
  basket,
  columnCount,
  onToggleActive,
  onProductsReorder,
}: {
  basket: Basket;
  columnCount: number;
  onToggleActive: (basketId: string, isActive: boolean) => void;
  onProductsReorder: (basketId: string, productIds: string[]) => Promise<void>;
}) {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const rowClasses = useRowStyles({ isOpen: open });
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: basket.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <>
      <TableRow ref={setNodeRef} style={style} className={rowClasses.basketRow} hover={!open} tabIndex={-1}>
        <TableCell align="center">
          <div className={classes.actions}>
            <div className={classes.drag}>
              <DragIndicatorIcon {...attributes} {...listeners} style={{ cursor: 'grab', opacity: 0.6 }} />
              <Chip className={classes.positionChip} size="small" label={basket.position} />
            </div>
            <IconButton
              size="small"
              aria-label="Afficher la composition"
              aria-expanded={open}
              onClick={() => setOpen((previous) => !previous)}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
            <IconButton
              size="small"
              aria-label="voir"
              component={Link}
              to={ADMIN_BASKETS_APP_PATHS.BASKET_DETAILS(basket.id)}
            >
              <ExitToAppIcon />
            </IconButton>
          </div>
        </TableCell>
        <TableCell align="center">
          <img src={BASKET_IMAGE_PATH(basket.photo)} alt={basket.name} width={40} height={40} />
        </TableCell>
        <TableCell align="left">
          {basket.name}
          <span className={classes.secondaryLine}>Réf. {basket.reference}</span>
        </TableCell>
        <TableCell align="left">{basket.subCategory.name}</TableCell>
        <TableCell align="right">{formatPrice(basket.price)}</TableCell>
        <TableCell align="center">
          <StockChip stock={basket.stock} />
        </TableCell>
        <BasketStatusCell basket={basket} onToggleActive={onToggleActive} />
      </TableRow>
      <TableRow className={rowClasses.collapseRow} tabIndex={-1}>
        <TableCell colSpan={columnCount}>
          <Collapse className={rowClasses.collapse} in={open} timeout="auto" unmountOnExit>
            <BasketHeaderSummary basket={basket} />
            <BasketProductsTable
              basketId={basket.id}
              products={basket.products}
              onProductsReorder={onProductsReorder}
            />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function BasketRow({
  basket,
  columnCount,
  onToggleActive,
  onProductsReorder,
}: {
  basket: Basket;
  columnCount: number;
  onToggleActive: (basketId: string, isActive: boolean) => void;
  onProductsReorder: (basketId: string, productIds: string[]) => Promise<void>;
}) {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const rowClasses = useRowStyles({ isOpen: open });

  return (
    <>
      <TableRow className={rowClasses.basketRow} hover={!open} tabIndex={-1}>
        <TableCell align="center">
          <div className={classes.actions}>
            <IconButton
              size="small"
              aria-label="Afficher la composition"
              aria-expanded={open}
              onClick={() => setOpen((previous) => !previous)}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
            <IconButton
              size="small"
              aria-label="voir"
              component={Link}
              to={ADMIN_BASKETS_APP_PATHS.BASKET_DETAILS(basket.id)}
            >
              <ExitToAppIcon />
            </IconButton>
          </div>
        </TableCell>
        <TableCell align="center">
          <img src={BASKET_IMAGE_PATH(basket.photo)} alt={basket.name} width={40} height={40} />
        </TableCell>
        <TableCell align="left">
          {basket.name}
          <span className={classes.secondaryLine}>Réf. {basket.reference}</span>
        </TableCell>
        <TableCell align="left">{basket.subCategory.name}</TableCell>
        <TableCell align="right">{formatPrice(basket.price)}</TableCell>
        <TableCell align="center">
          <StockChip stock={basket.stock} />
        </TableCell>
        <BasketStatusCell basket={basket} onToggleActive={onToggleActive} />
      </TableRow>
      <TableRow className={rowClasses.collapseRow} tabIndex={-1}>
        <TableCell colSpan={columnCount}>
          <Collapse className={rowClasses.collapse} in={open} timeout="auto" unmountOnExit>
            <BasketHeaderSummary basket={basket} />
            <BasketProductsTable
              basketId={basket.id}
              products={basket.products}
              onProductsReorder={onProductsReorder}
            />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function BasketsTable({
  baskets,
  totalCount,
  page,
  rowsPerPage,
  isLoading,
  isDragEnabled,
  onPageChange,
  onRowsPerPageChange,
  onPositionsChange,
  onToggleActive,
  onProductsReorder,
}: BasketsTableProps) {
  const [localBaskets, setLocalBaskets] = useState(baskets);

  useEffect(() => {
    setLocalBaskets(baskets);
  }, [baskets]);

  const handleToggleActive = async (basketId: string, isActive: boolean) => {
    const success = await onToggleActive(basketId, isActive);
    if (success) {
      setLocalBaskets((previous) =>
        previous.map((basket) => (basket.id === basketId ? { ...basket, isActive } : basket))
      );
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = localBaskets.findIndex((basket) => basket.id === active.id);
    const newIndex = localBaskets.findIndex((basket) => basket.id === over.id);

    const reordered = arrayMove(localBaskets, oldIndex, newIndex);
    const withPositions = reordered.map((basket, index) => ({
      ...basket,
      position: page * rowsPerPage + index + 1,
    }));

    setLocalBaskets(withPositions);
    onPositionsChange?.(withPositions.map((basket) => ({ id: basket.id, position: basket.position })));
  };

  const headers = getHeaders(isDragEnabled);
  const columnCount = headers.length;

  return (
    <Paper>
      <TableContainer style={{ maxHeight: '75svh' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {headers.map((header) => (
                <TableCell
                  key={header.key}
                  aria-label={header.ariaLabel}
                  align={header.align}
                  style={{ minWidth: header.minWidth, fontWeight: 'bold' }}
                >
                  {header.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {isLoading && <TableBodySkeleton rows={rowsPerPage} columns={columnCount} />}
          {!isLoading && (
            <TableBody>
              {localBaskets.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columnCount} style={{ textAlign: 'center' }}>
                    Aucun panier
                  </TableCell>
                </TableRow>
              )}
              {isDragEnabled ? (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext
                    items={localBaskets.map((basket) => basket.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {localBaskets.map((basket) => (
                      <SortableBasketRow
                        key={basket.id}
                        basket={basket}
                        columnCount={columnCount}
                        onToggleActive={handleToggleActive}
                        onProductsReorder={onProductsReorder}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              ) : (
                localBaskets.map((basket) => (
                  <BasketRow
                    key={basket.id}
                    basket={basket}
                    columnCount={columnCount}
                    onToggleActive={handleToggleActive}
                    onProductsReorder={onProductsReorder}
                  />
                ))
              )}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(event, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={(event) => onRowsPerPageChange(parseInt(event.target.value, 10))}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
      />
    </Paper>
  );
}

export default BasketsTable;
